import { useState } from 'react';
import type { ChimeraScan } from '../../services/chimeraLens';
import ScanFeedback from './ScanFeedback';

interface ChimeraLensProps {
  scan: ChimeraScan;
  mediaType: 'movie' | 'tv';
  mediaId: number;
}

export default function ChimeraLens({ scan, mediaType, mediaId }: ChimeraLensProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/scan/${mediaType}/${mediaId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can fail in insecure contexts or restricted browsers.
      // Surface the URL inline so the viewer can copy manually.
      window.prompt('Copy this scan link:', url);
    }
  };

  return (
    <section className="chimera-lens mt-8 max-w-3xl rounded-2xl border border-emerald-300/20 bg-black/30 p-5 shadow-2xl shadow-emerald-950/20 backdrop-blur-md">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-200">
            Body Scan
          </span>
          <span className="text-sm text-emerald-100/70">CHIMERA Lens</span>
        </div>
        <button
          type="button"
          onClick={handleShare}
          className="rounded-full border border-emerald-300/30 bg-emerald-300/5 px-3 py-1 text-xs text-emerald-100 transition hover:border-emerald-300/60 hover:bg-emerald-300/15"
        >
          {copied ? 'Copied' : 'Share scan'}
        </button>
      </div>

      <p className="mb-5 text-lg leading-relaxed text-white">{scan.signal}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <ScanLine label="Breath" value={scan.breath} />
        <ScanLine label="Membrane" value={scan.membrane} />
        <ScanLine label="Rhythm" value={scan.rhythm} />
        <ScanLine label="Gift" value={scan.gift} />
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200/80">Life Signal</p>
        <p className="mt-2 text-sm leading-relaxed text-gray-200">{scan.lesson}</p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {scan.cluster.map((item) => (
          <span key={item} className="rounded-full bg-emerald-300/10 px-3 py-1 text-xs text-emerald-100/80">
            {item}
          </span>
        ))}
      </div>

      <ScanFeedback mediaType={mediaType} mediaId={mediaId} clusters={scan.cluster} />
    </section>
  );
}

function ScanLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200/75">{label}</p>
      <p className="mt-1 text-sm leading-relaxed text-gray-200">{value}</p>
    </div>
  );
}
